import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import CreditPackage from './CreditPackage';
import FooterActions from './FooterActions';
import { ROUTES, TEXTS } from '../constants/uiConstants';
import { checkForUpdates } from '../services/updateService';
import logger from '../utils/logger';

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ CONSTANTS - REUSABLE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

const CHART_RETRY_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAYS: [5000, 15000, 60000], // 5s, 15s, 1min
  INITIAL_DELAY: 800,
  D9_DELAY_AFTER_D1: 2000
};

const MOBILE_BREAKPOINT = 700;
const UPDATE_CHECK_DELAY = 500;

const SERVICE_ITEMS = [
  {
    route: ROUTES.chat,
    iconUrl: "/uploads/chaticon.png",
    title: TEXTS.chatNow,
    subtitle: TEXTS.chatSubtitle
  },
  {
    route: ROUTES.call,
    iconUrl: "/uploads/bookicon.png",
    title: TEXTS.bookAppointment,
    subtitle: TEXTS.callSubtitle
  },
  //✅ REPORTS BUTTON
  {
    route: ROUTES.reports || '/reports',
    iconUrl: "/uploads/report.png",
    title: "Generate Reports",
    subtitle: "Detailed astrological insights"
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const canLoadChart = (user) => {
  return user?.userId && user?.birth_date && user?.birth_time;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ HOME COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    loadD1Chart,
    d1ChartData,
    d1ChartLoading,
    loadD9Chart,
    d9ChartData,
    d9ChartLoading
  } = useAuth();

  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  const lastUserIdRef = useRef(null);
  const d1LoadAttemptedRef = useRef(false);
  const d9LoadAttemptedRef = useRef(false);
  const loadingInProgressRef = useRef(false);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ PHASE 1: LOAD PAGE UI IMMEDIATELY
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    logger.log('\n🏠 ========================================');
    logger.log('🏠 HOME PAGE MOUNTED - UI READY');
    logger.log('🏠 ========================================\n');
    setPageLoaded(true);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ HANDLE WINDOW RESIZE
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ CHECK FOR UPDATES
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (!pageLoaded) return;

    const checkUpdates = async () => {
      try {
        const update = await checkForUpdates();
        if (update) {
          logger.log('🔔 Update available:', {
            current: update.currentVersion,
            latest: update.latestVersion,
            mandatory: update.mandatory
          });
          setUpdateInfo(update);
          setShowUpdatePopup(true);
        }
      } catch (error) {
        logger.error('⚠️ Update check failed:', error);
      }
    };

    setTimeout(checkUpdates, UPDATE_CHECK_DELAY);
  }, [pageLoaded]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ RESET TRACKING ON USER CHANGE
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (user?.userId && lastUserIdRef.current !== user.userId) {
      logger.log('\n🔄 ========================================');
      logger.log('🔄 NEW USER DETECTED - RESETTING CHART TRACKING');
      logger.log('🔄 Previous userId:', lastUserIdRef.current);
      logger.log('🔄 Current userId:', user.userId);
      logger.log('🔄 ========================================\n');
      
      lastUserIdRef.current = user.userId;
      d1LoadAttemptedRef.current = false;
      d9LoadAttemptedRef.current = false;
      loadingInProgressRef.current = false;
    }
  }, [user?.userId]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ PHASE 2: SEQUENTIAL CHART LOADING (D1 → D9)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    // Prevent multiple simultaneous executions
    if (loadingInProgressRef.current) {
      logger.log('⏸️ Chart loading already in progress, skipping...');
      return;
    }

    logger.log('\n📊 ========== CHART LOADING EFFECT TRIGGERED ==========');
    logger.log('📊 Effect State:', {
      pageLoaded,
      hasUser: !!user,
      userId: user?.userId,
      canLoad: canLoadChart(user),
      d1ChartExists: !!d1ChartData,
      d1Loading: d1ChartLoading,
      d9ChartExists: !!d9ChartData,
      d9Loading: d9ChartLoading,
      d1Attempted: d1LoadAttemptedRef.current,
      d9Attempted: d9LoadAttemptedRef.current,
      loadingInProgress: loadingInProgressRef.current
    });

    // ✅ Early exit checks
    if (!pageLoaded) {
      logger.log('⏸️ Page not loaded yet, skipping chart load');
      return;
    }

    if (!canLoadChart(user)) {
      if (user?.userId) {
        logger.log('⚠️ Cannot load charts: Missing birth data', {
          hasBirthDate: !!user?.birth_date,
          hasBirthTime: !!user?.birth_time
        });
      }
      return;
    }

    // ✅ Load charts sequentially
    const loadChartsSequentially = async () => {
      loadingInProgressRef.current = true;

      try {
        // ═══════════════════════════════════════════════════════════════════
        // ✅ STEP 1: LOAD D1 CHART
        // ═══════════════════════════════════════════════════════════════════
        
        if (!d1ChartData && !d1ChartLoading && !d1LoadAttemptedRef.current) {
          logger.log('\n📊 ========== STARTING D1 CHART LOAD ==========');
          logger.log('📊 D1 Load Initiated:', {
            userId: user.userId,
            hasData: !!d1ChartData,
            isLoading: d1ChartLoading,
            alreadyAttempted: d1LoadAttemptedRef.current
          });

          d1LoadAttemptedRef.current = true;
          
          await new Promise(resolve => setTimeout(resolve, CHART_RETRY_CONFIG.INITIAL_DELAY));
          
          logger.log('📊 Calling loadD1Chart()...');
          const d1Result = await loadD1Chart();
          
          if (d1Result) {
            logger.log('✅ D1 chart load completed successfully:', {
              chartId: d1Result.d1ChartsId,
              source: d1Result.source
            });
          } else {
            logger.warn('⚠️ D1 chart load returned null');
          }
          
          logger.log('📊 ========== D1 CHART LOAD COMPLETE ==========\n');
        } else if (d1ChartData) {
          logger.log('✅ D1 chart already loaded, chartId:', d1ChartData.d1ChartsId);
        } else if (d1ChartLoading) {
          logger.log('⏳ D1 chart is currently loading, waiting...');
          return; // Exit and wait for next effect run
        }

        // ═══════════════════════════════════════════════════════════════════
        // ✅ STEP 2: LOAD D9 CHART (AFTER D1)
        // ═══════════════════════════════════════════════════════════════════
        
        if (d1ChartData && !d9ChartData && !d9ChartLoading && !d9LoadAttemptedRef.current) {
          logger.log('\n📊 ========== STARTING D9 CHART LOAD ==========');
          logger.log('📊 D9 Load Initiated:', {
            userId: user.userId,
            d1ChartId: d1ChartData.d1ChartsId,
            d1Exists: !!d1ChartData,
            hasD9Data: !!d9ChartData,
            isLoading: d9ChartLoading,
            alreadyAttempted: d9LoadAttemptedRef.current
          });

          d9LoadAttemptedRef.current = true;
          
          logger.log(`⏳ Waiting ${CHART_RETRY_CONFIG.D9_DELAY_AFTER_D1}ms before loading D9...`);
          await new Promise(resolve => setTimeout(resolve, CHART_RETRY_CONFIG.D9_DELAY_AFTER_D1));
          
          logger.log('📊 Calling loadD9Chart()...');
          const d9Result = await loadD9Chart();
          
          if (d9Result) {
            logger.log('✅ D9 chart load completed successfully:', {
              chartId: d9Result.d9ChartsId,
              source: d9Result.source,
              grahaCount: d9Result.graha_table?.length || 0
            });
          } else {
            logger.warn('⚠️ D9 chart load returned null');
            // Reset attempt flag to allow retry
            d9LoadAttemptedRef.current = false;
          }
          
          logger.log('📊 ========== D9 CHART LOAD COMPLETE ==========\n');
        } else if (!d1ChartData) {
          logger.log('⏳ Waiting for D1 chart before loading D9...');
        } else if (d9ChartData) {
          logger.log('✅ D9 chart already loaded, chartId:', d9ChartData.d9ChartsId);
        } else if (d9ChartLoading) {
          logger.log('⏳ D9 chart is currently loading, waiting...');
        }

        logger.log('📊 ========== CHART LOADING EFFECT COMPLETE ==========\n');

      } catch (error) {
        logger.error(' Chart loading failed:', error);
        
        // ✅ Reset attempt flags on error to allow retry
        if (!d1ChartData) {
          d1LoadAttemptedRef.current = false;
        }
        if (!d9ChartData) {
          d9LoadAttemptedRef.current = false;
        }
      } finally {
        loadingInProgressRef.current = false;
      }
    };

    loadChartsSequentially();

  }, [
    pageLoaded,
    user?.userId,
    user?.birth_date,
    user?.birth_time,
    d1ChartData,
    d1ChartLoading,
    d9ChartData,
    d9ChartLoading,
    loadD1Chart,
    loadD9Chart
  ]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOG CHART STATUS CHANGES
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (d1ChartData) {
      logger.log('\n✅ ========== D1 CHART IN HOME CONTEXT ==========');
      logger.log('✅ D1 Chart Data:', {
        chartId: d1ChartData.d1ChartsId,
        source: d1ChartData.source,
        ayanamsa: d1ChartData.ayanamsa,
        sunSign: d1ChartData.sun_sign,
        moonSign: d1ChartData.moon_sign,
        grahaCount: d1ChartData.graha_table?.length || 0,
        loadedAt: d1ChartData.loadedAt
      });
      logger.log('✅ ==========================================\n');
    }
  }, [d1ChartData]);

  useEffect(() => {
    if (d9ChartData) {
      logger.log('\n✅ ========== D9 CHART IN HOME CONTEXT ==========');
      logger.log('✅ D9 Chart Data:', {
        chartId: d9ChartData.d9ChartsId,
        source: d9ChartData.source,
        ayanamsa: d9ChartData.ayanamsa,
        chartType: d9ChartData.chart_type,
        grahaCount: d9ChartData.graha_table?.length || 0,
        grahaTableType: typeof d9ChartData.graha_table,
        grahaTableIsArray: Array.isArray(d9ChartData.graha_table),
        loadedAt: d9ChartData.loadedAt,
        dataKeys: Object.keys(d9ChartData)
      });
      
      // ✅ Log sample planet data if available
      if (Array.isArray(d9ChartData.graha_table) && d9ChartData.graha_table.length > 0) {
        logger.log('✅ D9 Sample Planet Data:', {
          firstPlanet: d9ChartData.graha_table[0]?.graha,
          firstPlanetHouse: d9ChartData.graha_table[0]?.isIn,
          totalPlanets: d9ChartData.graha_table.length
        });
      }
      
      logger.log('✅ ==========================================\n');
    }
  }, [d9ChartData]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ NAVIGATION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const goTo = (route) => () => {
    logger.log('🔗 Navigating to:', route);
    navigate(route);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ UPDATE POPUP HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const handleUpdateNow = () => {
    logger.log('🔔 User clicked Update Now');
    if (updateInfo?.openPlayStore) {
      updateInfo.openPlayStore();
    }
    setShowUpdatePopup(false);
  };

  const handleUpdateLater = () => {
    logger.log('🔔 User clicked Update Later');
    if (!updateInfo?.mandatory) {
      setShowUpdatePopup(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  
  return (
    <>
      <div className="large-container">
        <header className="home-hero">
          <div className="hero-content">
            <h1>
              {TEXTS.welcome} {user?.full_name?.split(' ')[0] ?? ''}
            </h1>
            <p>{TEXTS.tagline}</p>
          </div>
        </header>

        <main className="home-main">
          {isMobile ? (
            <MobileServiceScroll items={SERVICE_ITEMS} goTo={goTo} />
          ) : (
            <DesktopServiceGrid items={SERVICE_ITEMS} goTo={goTo} />
          )}
        </main>

        <FooterActions
          onCredits={goTo(ROUTES.credits)}
          onRefer={goTo(ROUTES.refer)}
        />
      </div>

      {showUpdatePopup && updateInfo && (
        <UpdatePopup
          updateInfo={updateInfo}
          onUpdateNow={handleUpdateNow}
          onUpdateLater={handleUpdateLater}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const MobileServiceScroll = ({ items, goTo }) => (
  <div className="infinite-scroll-wrapper">
    <div className="infinite-scroll-track">
      {/* First set of items */}
      {items.map((item, index) => (
        <div key={`first-${index}`} className="infinite-scroll-item">
          <CreditPackage
            onClick={goTo(item.route)}
            iconUrl={item.iconUrl}
            title={item.title}
            subtitle={item.subtitle}
          />
        </div>
      ))}
      {/* Duplicate set for seamless loop */}
      {items.map((item, index) => (
        <div key={`second-${index}`} className="infinite-scroll-item">
          <CreditPackage
            onClick={goTo(item.route)}
            iconUrl={item.iconUrl}
            title={item.title}
            subtitle={item.subtitle}
          />
        </div>
      ))}
    </div>
  </div>
);

const DesktopServiceGrid = ({ items, goTo }) => (
  <div className="home-packages-grid" role="navigation" aria-label="Primary services">
    {items.map((item, index) => (
      <CreditPackage
        key={index}
        onClick={goTo(item.route)}
        iconUrl={item.iconUrl}
        title={item.title}
        subtitle={item.subtitle}
      />
    ))}
  </div>
);

const UpdatePopup = ({ updateInfo, onUpdateNow, onUpdateLater }) => {
  return (
    <div className="modal-overlay">
      <div className="message-popup-modal">
        {!updateInfo.mandatory && (
          <button className="modal-close-btn" onClick={onUpdateLater}>
            ×
          </button>
        )}

        <div className="message-popup-content">
          <div className="feedback-title">
            {updateInfo.mandatory ? 'Update Required' : 'New Update Available!'}
          </div>

          <div className="scroll-picker-modal" style={{ margin: '15px 0', padding: '10px' }}>
            <VersionDisplay
              label="Current Version"
              version={updateInfo.currentVersion}
              color="black"
            />
            <VersionDisplay
              label="Latest Version"
              version={updateInfo.latestVersion}
              color="#4CAF50"
            />
          </div>

          <div className="message-text">{updateInfo.releaseNotes}</div>

          {updateInfo.mandatory && (
            <MandatoryUpdateWarning />
          )}

          <div className="feedback-actions">
            {!updateInfo.mandatory && (
              <button className="feature-item" onClick={onUpdateLater} style={{ color: '#fff' }}>
                Later
              </button>
            )}
            <button className="book-call-btn" onClick={onUpdateNow}>
              Update Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VersionDisplay = ({ label, version, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
    <span style={{ fontWeight: '600', color }}>{label}:</span>
    <span style={{ fontWeight: '600', color }}>v{version}</span>
  </div>
);

const MandatoryUpdateWarning = () => (
  <div style={{ color: '#f44336', fontSize: '13px', marginTop: '10px', fontWeight: '500' }}>
    ⚠️ This update is required to continue using the app
  </div>
);

export default Home;
